import type React from "react";
import { useRef, useMemo, useEffect, useState, Suspense, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import * as THREE from "three";
import { RiFullscreenLine, RiFullscreenExitLine } from "react-icons/ri";
import type { Fixation, Saccade } from "../../types/gazeHeatmapTypes";
import type { HotSpot } from "../../types/coverageStatistics";
import ScanpathOverlay3D from "./ScanpathOverlay3D";
import {
  CursorClickIcon,
  ExclamationIcon,
  MinusSmIcon,
  PlusSmIcon,
} from "@heroicons/react/outline";

/** Video error info when resolution exceeds GPU limits */
interface VideoSizeError {
  width: number;
  height: number;
  maxTextureSize: number;
}

interface InteractiveViewer360Props {
  mediaUrl: string;
  mediaType?: "image" | "video";
  heatmapImageData: ImageData | null;
  opacity: number;
  initialRotationY?: number;
  videoSegmentStart?: number;
  videoSegmentEnd?: number;
  onVideoReady?: () => void;
  onTimeUpdate?: (currentTime: number) => void;
  videoRef?: React.MutableRefObject<HTMLVideoElement | null>;
  /** Scanpath fixations to display */
  scanpathFixations?: Fixation[];
  /** Scanpath saccades to display */
  scanpathSaccades?: Saccade[];
  /** Current fixation (highlighted) */
  currentFixation?: Fixation | null;
  /** Whether to show scanpath overlay */
  showScanpath?: boolean;
  /** Hot spot being hovered in stats panel */
  hoveredHotSpot?: HotSpot | null;
  /** Show fullscreen toggle button */
  showFullscreenButton?: boolean;
}

/**
 * Custom shader material for blending background texture with heatmap overlay
 */
const HeatmapBlendMaterial: React.FC<{
  backgroundTexture: THREE.Texture;
  heatmapTexture: THREE.Texture | null;
  opacity: number;
  /** Horizontal offset for heatmap texture (0-1, used to align heatmap with video) */
  heatmapUOffset?: number;
}> = ({ backgroundTexture, heatmapTexture, opacity, heatmapUOffset = 0 }) => {
  const materialRef = useRef<THREE.ShaderMaterial>(null);

  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.uniforms.tBackground.value = backgroundTexture;
      materialRef.current.uniforms.tHeatmap.value = heatmapTexture;
      materialRef.current.uniforms.uOpacity.value = opacity;
      materialRef.current.uniforms.uHeatmapOffset.value = heatmapUOffset;
      materialRef.current.needsUpdate = true;
    }
  }, [backgroundTexture, heatmapTexture, opacity, heatmapUOffset]);

  const shaderConfig = useMemo(
    () => ({
      uniforms: {
        tBackground: { value: backgroundTexture },
        tHeatmap: { value: heatmapTexture },
        uOpacity: { value: opacity },
        uHeatmapOffset: { value: heatmapUOffset },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tBackground;
        uniform sampler2D tHeatmap;
        uniform float uOpacity;
        uniform float uHeatmapOffset;
        varying vec2 vUv;

        void main() {
          // Flip U coordinate horizontally to correct for BackSide rendering
          vec2 correctedUv = vec2(1.0 - vUv.x, vUv.y);

          // Background uses standard UV
          vec4 bg = texture2D(tBackground, correctedUv);

          // Heatmap uses offset UV (for video alignment)
          // Subtract offset to match the scanpath direction (which also subtracts due to sphereU = 1 - adjustedU)
          vec2 hmUv = correctedUv;
          hmUv.x = fract(hmUv.x - uHeatmapOffset + 1.0); // +1.0 ensures positive before fract
          vec4 hm = texture2D(tHeatmap, hmUv);

          // Blend using heatmap alpha and global opacity
          float alpha = hm.a * uOpacity;
          gl_FragColor = mix(bg, vec4(hm.rgb, 1.0), alpha);
        }
      `,
      side: THREE.BackSide,
    }),
    [backgroundTexture, heatmapTexture, opacity, heatmapUOffset],
  );

  // eslint-disable-next-line react/no-unknown-property -- react-three-fiber intrinsic props (attach/args), not DOM attributes
  return <shaderMaterial ref={materialRef} attach="material" args={[shaderConfig]} />;
};

/**
 * The 360° sphere with textures (supports both image and video)
 */
/**
 * Video horizontal offset to align heatmap/scanpath with video content.
 * This compensates for differences between Unity's AVProVideo shader
 * and Three.js BackSide sphere rendering.
 *
 * Value is in UV space [0, 1] where 0.25 = 90°, 0.5 = 180°, etc.
 */
const VIDEO_U_OFFSET_NO_ROTATION = 0.28;
const VIDEO_U_OFFSET_WITH_ROTATION = 0.496;

function getVideoUOffset(rotationY: number): number {
  return rotationY === 0 ? VIDEO_U_OFFSET_NO_ROTATION : VIDEO_U_OFFSET_WITH_ROTATION;
}

const Sphere360: React.FC<{
  mediaUrl: string;
  mediaType: "image" | "video";
  heatmapImageData: ImageData | null;
  opacity: number;
  videoSegmentStart?: number;
  videoSegmentEnd?: number;
  onVideoReady?: () => void;
  onTimeUpdate?: (currentTime: number) => void;
  externalVideoRef?: React.MutableRefObject<HTMLVideoElement | null>;
  onVideoSizeError?: (error: VideoSizeError) => void;
  rotationY?: number;
}> = ({
  mediaUrl,
  mediaType,
  heatmapImageData,
  opacity,
  videoSegmentStart,
  videoSegmentEnd,
  onVideoReady,
  onTimeUpdate,
  externalVideoRef,
  onVideoSizeError,
  rotationY = 0,
}) => {
  const [backgroundTexture, setBackgroundTexture] = useState<THREE.Texture | null>(null);
  const [heatmapTexture, setHeatmapTexture] = useState<THREE.Texture | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoTextureRef = useRef<THREE.VideoTexture | null>(null);
  const { gl } = useThree();
  const segmentStartRef = useRef<number>(videoSegmentStart || 0);
  const segmentEndRef = useRef<number>(videoSegmentEnd || 0);

  useEffect(() => {
    segmentStartRef.current = videoSegmentStart || 0;
    segmentEndRef.current = videoSegmentEnd || 0;
  }, [videoSegmentStart, videoSegmentEnd]);

  const lastTimeUpdateRef = useRef<number>(-1);

  useFrame(() => {
    if (videoTextureRef.current && videoRef.current) {
      const video = videoRef.current;

      if (segmentEndRef.current > 0 && video.currentTime >= segmentEndRef.current) {
        video.currentTime = segmentStartRef.current;
      }

      if (!video.paused) {
        videoTextureRef.current.needsUpdate = true;

        const currentTime = video.currentTime;
        if (onTimeUpdate && Math.abs(currentTime - lastTimeUpdateRef.current) > 0.016) {
          lastTimeUpdateRef.current = currentTime;
          onTimeUpdate(currentTime);
        }
      }
    }
  });

  useEffect(() => {
    if (videoRef.current && mediaType === "video" && videoSegmentStart !== undefined) {
      videoRef.current.currentTime = videoSegmentStart;
    }
  }, [videoSegmentStart, mediaType]);

  useEffect(() => {
    if (mediaType === "video") {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.loop = false;
      video.muted = true;
      video.playsInline = true;
      video.src = mediaUrl;

      videoRef.current = video;

      if (externalVideoRef) {
        externalVideoRef.current = video;
      }

      let videoRejected = false;

      video.addEventListener("loadedmetadata", () => {
        const maxTextureSize = gl.capabilities.maxTextureSize;
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;

        const safeMaxTextureSize = Math.min(maxTextureSize, 4096);

        if (videoWidth > safeMaxTextureSize || videoHeight > safeMaxTextureSize) {
          console.error(
            `Video resolution (${videoWidth}x${videoHeight}) exceeds safe texture size (${safeMaxTextureSize}x${safeMaxTextureSize})`,
          );
          videoRejected = true;
          video.pause();
          video.src = "";
          onVideoSizeError?.({
            width: videoWidth,
            height: videoHeight,
            maxTextureSize: safeMaxTextureSize,
          });
        }
      });

      video.addEventListener("loadeddata", () => {
        if (videoRejected) return;

        const texture = new THREE.VideoTexture(video);
        texture.encoding = THREE.LinearEncoding;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        videoTextureRef.current = texture;
        setBackgroundTexture(texture);

        if (videoSegmentStart !== undefined && videoSegmentStart > 0) {
          video.currentTime = videoSegmentStart;
        }

        video.play().catch((e) => console.warn("Video autoplay failed:", e));
        onVideoReady?.();
      });

      video.addEventListener("ended", () => {
        video.currentTime = segmentStartRef.current;
        video.play().catch((e) => console.warn("Video replay failed:", e));
      });

      video.addEventListener("error", (e) => {
        console.error("Error loading video:", e);
      });

      const handleSeeked = () => {
        if (videoTextureRef.current) {
          videoTextureRef.current.needsUpdate = true;
        }
        if (onTimeUpdate) {
          lastTimeUpdateRef.current = video.currentTime;
          onTimeUpdate(video.currentTime);
        }
      };
      video.addEventListener("seeked", handleSeeked);

      return () => {
        video.pause();
        video.src = "";
        video.removeEventListener("seeked", handleSeeked);
        videoRef.current = null;
        if (externalVideoRef) {
          externalVideoRef.current = null;
        }
        videoTextureRef.current?.dispose();
        videoTextureRef.current = null;
      };
    } else {
      const loader = new THREE.TextureLoader();
      loader.crossOrigin = "anonymous";
      loader.load(
        mediaUrl,
        (texture) => {
          texture.encoding = THREE.LinearEncoding;
          setBackgroundTexture(texture);
        },
        undefined,
        (error) => {
          console.error("Error loading 360 texture:", error);
        },
      );

      return () => {
        backgroundTexture?.dispose();
      };
    }
  }, [mediaUrl, mediaType, onVideoReady, onTimeUpdate]);

  useEffect(() => {
    if (!heatmapImageData) {
      setHeatmapTexture(null);
      return undefined;
    }

    const canvas = document.createElement("canvas");
    canvas.width = heatmapImageData.width;
    canvas.height = heatmapImageData.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return undefined;
    }

    ctx.putImageData(heatmapImageData, 0, 0);
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    setHeatmapTexture(texture);

    return () => {
      texture.dispose();
    };
  }, [heatmapImageData]);

  if (!backgroundTexture) {
    return null;
  }

  const heatmapUOffset = mediaType === "video" ? getVideoUOffset(rotationY) : 0;

  return (
    <mesh>
      {/* eslint-disable-next-line react/no-unknown-property -- react-three-fiber intrinsic prop (args), not a DOM attribute */}
      <sphereGeometry args={[500, 64, 32]} />
      <HeatmapBlendMaterial
        backgroundTexture={backgroundTexture}
        heatmapTexture={heatmapTexture}
        opacity={opacity}
        heatmapUOffset={heatmapUOffset}
      />
    </mesh>
  );
};

/**
 * Renderer setup (colors only, camera handled by OrbitControls)
 */
const RendererSetup: React.FC = () => {
  const { gl } = useThree();

  useEffect(() => {
    gl.toneMapping = THREE.NoToneMapping;
    gl.outputEncoding = THREE.LinearEncoding;
  }, [gl]);

  return null;
};

const DEFAULT_FOV = 75;
const MIN_FOV = 30;
const MAX_FOV = 100;
const ZOOM_STEP = 10;

/**
 * OrbitControls wrapper with initial rotation support
 */
const Controls: React.FC<{
  initialRotationY: number;
  mediaType: "image" | "video";
  onZoomChange?: (zoomLevel: number) => void;
  controlsRef?: React.MutableRefObject<OrbitControlsImpl | null>;
}> = ({ initialRotationY, mediaType, onZoomChange, controlsRef: externalControlsRef }) => {
  const internalControlsRef = useRef<OrbitControlsImpl>(null);
  const controlsRef = externalControlsRef || internalControlsRef;
  const { camera } = useThree();
  const lastRotationRef = useRef<number | null>(null);
  const lastMediaTypeRef = useRef<string | null>(null);

  useFrame(() => {
    if (camera instanceof THREE.PerspectiveCamera && onZoomChange) {
      const zoomPercent = Math.round(((MAX_FOV - camera.fov) / (MAX_FOV - MIN_FOV)) * 100);
      onZoomChange(zoomPercent);
    }
  });

  useEffect(() => {
    if (
      controlsRef.current &&
      (lastRotationRef.current !== initialRotationY || lastMediaTypeRef.current !== mediaType)
    ) {
      const azimuthAngle = (initialRotationY * Math.PI) / 180 + (264.5 * Math.PI) / 180;

      const distance = 0.1;
      camera.position.setFromSphericalCoords(distance, Math.PI / 2, azimuthAngle);
      camera.lookAt(0, 0, 0);
      controlsRef.current.update();
      lastRotationRef.current = initialRotationY;
      lastMediaTypeRef.current = mediaType;
    }
  }, [initialRotationY, mediaType, camera, controlsRef]);

  return (
    <OrbitControls
      ref={controlsRef}
      enableZoom={false}
      enablePan={false}
      rotateSpeed={-0.3}
      minPolarAngle={Math.PI * 0.1}
      maxPolarAngle={Math.PI * 0.9}
      target={[0, 0, 0]}
      mouseButtons={{
        LEFT: THREE.MOUSE.PAN,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.ROTATE,
      }}
    />
  );
};

/**
 * Zoom controls component for manual zoom adjustment
 */
const ZoomControlsOverlay: React.FC<{
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
}> = ({ zoomLevel, onZoomIn, onZoomOut, onReset }) => {
  const { t } = useTranslation();
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  const modifierKey = isMac ? "Cmd" : "Alt";

  return (
    <div className="absolute top-4 right-4 flex flex-col items-center gap-1 bg-black/60 backdrop-blur-sm rounded-lg p-1.5 z-10">
      <button
        onClick={onZoomIn}
        className="text-white hover:text-green-400 transition-colors focus:outline-none p-1"
        title={t("statsNew.gazeHeatmap.zoomIn")}
      >
        <PlusSmIcon className="h-5 w-5" strokeWidth={2} />
      </button>

      <button
        onClick={onReset}
        className="text-white text-xs font-mono px-1.5 py-0.5 hover:bg-white/20 rounded transition-colors"
        title={t("statsNew.gazeHeatmap.resetZoom")}
      >
        {zoomLevel}%
      </button>

      <button
        onClick={onZoomOut}
        className="text-white hover:text-green-400 transition-colors focus:outline-none p-1"
        title={t("statsNew.gazeHeatmap.zoomOut")}
      >
        <MinusSmIcon className="h-5 w-5" strokeWidth={2} />
      </button>

      <div className="text-white/60 text-[10px] mt-1 text-center leading-tight">
        {modifierKey} + scroll
      </div>
    </div>
  );
};

/**
 * Scene-level component that manages zoom and exposes controls
 */
const ZoomManager: React.FC<{
  initialRotationY: number;
  mediaType: "image" | "video";
  onZoomLevelChange: (level: number) => void;
  zoomIn: React.MutableRefObject<(() => void) | null>;
  zoomOut: React.MutableRefObject<(() => void) | null>;
  resetZoom: React.MutableRefObject<(() => void) | null>;
}> = ({ initialRotationY, mediaType, onZoomLevelChange, zoomIn, zoomOut, resetZoom }) => {
  const { camera, gl } = useThree();

  useEffect(() => {
    zoomIn.current = () => {
      if (camera instanceof THREE.PerspectiveCamera) {
        camera.fov = Math.max(MIN_FOV, camera.fov - ZOOM_STEP);
        camera.updateProjectionMatrix();
      }
    };

    zoomOut.current = () => {
      if (camera instanceof THREE.PerspectiveCamera) {
        camera.fov = Math.min(MAX_FOV, camera.fov + ZOOM_STEP);
        camera.updateProjectionMatrix();
      }
    };

    resetZoom.current = () => {
      if (camera instanceof THREE.PerspectiveCamera) {
        camera.fov = DEFAULT_FOV;
        camera.updateProjectionMatrix();
      }
    };
  }, [camera, zoomIn, zoomOut, resetZoom]);

  useEffect(() => {
    const canvas = gl.domElement;

    const handleWheel = (e: WheelEvent) => {
      if (e.metaKey || e.altKey) {
        e.preventDefault();
        e.stopPropagation();

        if (camera instanceof THREE.PerspectiveCamera) {
          const zoomAmount = e.deltaY > 0 ? 3 : -3;
          camera.fov = Math.max(MIN_FOV, Math.min(MAX_FOV, camera.fov + zoomAmount));
          camera.updateProjectionMatrix();
        }
      }
    };

    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleWheel);
  }, [camera, gl]);

  return (
    <Controls
      initialRotationY={initialRotationY}
      mediaType={mediaType}
      onZoomChange={onZoomLevelChange}
    />
  );
};

/**
 * Convert UV coordinates to 3D position on a sphere (for hot spot marker)
 * Uses the same formula as ScanpathOverlay3D for consistency.
 *
 * This matches Three.js SphereGeometry's UV mapping and accounts for
 * the shader's BackSide correction (correctedUv = 1-vUv.x, vUv.y).
 */
function uvToSpherePosition(u: number, v: number, radius: number, uOffset = 0): THREE.Vector3 {
  let adjustedU = u + uOffset;
  if (adjustedU > 1) adjustedU -= 1;
  if (adjustedU < 0) adjustedU += 1;

  const sphereU = 1.0 - adjustedU;
  const sphereV = 1.0 - v;

  const theta = sphereU * 2 * Math.PI;
  const phi = sphereV * Math.PI;

  const innerRadius = radius * 0.98;

  const x = -innerRadius * Math.cos(theta) * Math.sin(phi);
  const y = innerRadius * Math.cos(phi);
  const z = innerRadius * Math.sin(theta) * Math.sin(phi);

  return new THREE.Vector3(x, y, z);
}

/**
 * 3D marker for hovered hot spot
 */
const HotSpotMarker3D: React.FC<{
  hotSpot: HotSpot;
  sphereRadius: number;
  uOffset?: number;
}> = ({ hotSpot, sphereRadius, uOffset = 0 }) => {
  const position = useMemo(
    () => uvToSpherePosition(hotSpot.center.u, hotSpot.center.v, sphereRadius, uOffset),
    [hotSpot.center.u, hotSpot.center.v, sphereRadius, uOffset],
  );

  return (
    // eslint-disable-next-line react/no-unknown-property -- react-three-fiber intrinsic prop (position), not a DOM attribute
    <group position={position}>
      <mesh>
        {/* eslint-disable-next-line react/no-unknown-property -- react-three-fiber intrinsic prop (args), not a DOM attribute */}
        <ringGeometry args={[18, 22, 32]} />
        {/* eslint-disable-next-line react/no-unknown-property -- react-three-fiber intrinsic props (transparent/side), not DOM attributes */}
        <meshBasicMaterial color="#ef4444" opacity={0.6} transparent side={THREE.DoubleSide} />
      </mesh>
      <mesh>
        {/* eslint-disable-next-line react/no-unknown-property -- react-three-fiber intrinsic prop (args), not a DOM attribute */}
        <circleGeometry args={[12, 32]} />
        {/* eslint-disable-next-line react/no-unknown-property -- react-three-fiber intrinsic props (transparent/side), not DOM attributes */}
        <meshBasicMaterial color="#ef4444" opacity={0.3} transparent side={THREE.DoubleSide} />
      </mesh>
      <Html center style={{ pointerEvents: "none" }}>
        <div
          style={{
            backgroundColor: "rgba(239, 68, 68, 0.9)",
            color: "white",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "bold",
            whiteSpace: "nowrap",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}
        >
          {hotSpot.attentionPercentage.toFixed(1)}%
        </div>
      </Html>
    </group>
  );
};

/**
 * Interactive 360° viewer component using Three.js
 * Renders the equirectangular image or video on the inside of a sphere with orbit controls
 */
const InteractiveViewer360: React.FC<InteractiveViewer360Props> = ({
  mediaUrl,
  mediaType = "image",
  heatmapImageData,
  opacity,
  initialRotationY = 0,
  videoSegmentStart,
  videoSegmentEnd,
  onVideoReady,
  onTimeUpdate,
  videoRef: externalVideoRef,
  scanpathFixations = [],
  scanpathSaccades = [],
  currentFixation,
  showScanpath = false,
  hoveredHotSpot,
  showFullscreenButton = false,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showHint, setShowHint] = useState(true);
  const [videoSizeError, setVideoSizeError] = useState<VideoSizeError | null>(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const zoomInRef = useRef<(() => void) | null>(null);
  const zoomOutRef = useRef<(() => void) | null>(null);
  const resetZoomRef = useRef<(() => void) | null>(null);

  const handleZoomIn = useCallback(() => zoomInRef.current?.(), []);
  const handleZoomOut = useCallback(() => zoomOutRef.current?.(), []);
  const handleResetZoom = useCallback(() => resetZoomRef.current?.(), []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(console.error);
    } else {
      void document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    setVideoSizeError(null);
  }, [mediaUrl]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 2) setShowHint(false);
    };
    window.addEventListener("mousedown", handleMouseDown);
    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0 w-full h-full bg-black">
      <Canvas
        camera={{
          fov: 75,
          near: 0.1,
          far: 1000,
          position: [0, 0, 0.1],
        }}
        gl={{
          antialias: true,
          alpha: false,
          powerPreference: "high-performance",
          preserveDrawingBuffer: true,
        }}
      >
        <Suspense fallback={null}>
          <RendererSetup />
          <Sphere360
            mediaUrl={mediaUrl}
            mediaType={mediaType}
            heatmapImageData={heatmapImageData}
            opacity={opacity}
            videoSegmentStart={videoSegmentStart}
            videoSegmentEnd={videoSegmentEnd}
            onVideoReady={onVideoReady}
            onTimeUpdate={onTimeUpdate}
            externalVideoRef={externalVideoRef}
            onVideoSizeError={setVideoSizeError}
            rotationY={initialRotationY}
          />
          <ZoomManager
            initialRotationY={initialRotationY}
            mediaType={mediaType}
            onZoomLevelChange={setZoomLevel}
            zoomIn={zoomInRef}
            zoomOut={zoomOutRef}
            resetZoom={resetZoomRef}
          />

          {showScanpath && scanpathFixations.length > 0 && (
            <ScanpathOverlay3D
              fixations={scanpathFixations}
              saccades={scanpathSaccades}
              currentFixation={currentFixation}
              sphereRadius={500}
              sceneRotationY={initialRotationY}
              opacity={opacity}
              uOffset={mediaType === "video" ? getVideoUOffset(initialRotationY) : 0}
            />
          )}

          {hoveredHotSpot && (
            <HotSpotMarker3D
              hotSpot={hoveredHotSpot}
              sphereRadius={500}
              uOffset={mediaType === "video" ? getVideoUOffset(initialRotationY) : 0}
            />
          )}
        </Suspense>
      </Canvas>

      {!videoSizeError && (
        <ZoomControlsOverlay
          zoomLevel={zoomLevel}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onReset={handleResetZoom}
        />
      )}

      {showFullscreenButton && !videoSizeError && (
        <button
          onClick={toggleFullscreen}
          className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm rounded-lg p-2 z-10 text-white hover:text-green-400 transition-colors focus:outline-none"
          title={t(
            isFullscreen
              ? "statsNew.gazeHeatmap.exitFullscreen"
              : "statsNew.gazeHeatmap.fullscreen",
          )}
        >
          {isFullscreen ? <RiFullscreenExitLine size={20} /> : <RiFullscreenLine size={20} />}
        </button>
      )}

      {showHint && !videoSizeError && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 bg-black/60 text-white text-sm px-4 py-2 rounded-full backdrop-blur-sm">
          <CursorClickIcon className="h-4 w-4" strokeWidth={1.5} />
          <span>{t("statsNew.gazeHeatmap.rightClickHint")}</span>
        </div>
      )}

      {videoSizeError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/90">
          <div className="bg-white rounded-lg p-6 max-w-md text-center shadow-xl">
            <ExclamationIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("statsNew.sessionDetail.videoTooLarge")}
            </h3>
            <p className="text-sm text-gray-600 mb-1">
              {t("statsNew.sessionDetail.videoResolution", {
                width: videoSizeError.width,
                height: videoSizeError.height,
              })}
            </p>
            <p className="text-sm text-gray-600">
              {t("statsNew.sessionDetail.maxSupported", {
                max: videoSizeError.maxTextureSize,
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveViewer360;
